import { useEffect, type RefObject } from 'react';

/**
 * Closes an open panel on a press outside `ref`, and swallows the click that
 * press produces, so the tap only closes the panel (like the backdrop the
 * pending-transactions floater had) instead of also hitting what is behind it.
 */
export function useOutsidePressDismiss(ref: RefObject<HTMLElement | null>, open: boolean, onDismiss: () => void): void {
  useEffect(() => {
    if (!open) return;
    let release: (() => void) | null = null;
    const onMouseDown = (event: MouseEvent) => {
      if (!ref.current || ref.current.contains(event.target as Node)) return;
      const swallow = (click: MouseEvent) => {
        click.preventDefault();
        click.stopPropagation();
        release?.();
      };
      // The click follows this press; drop the guard if none comes (e.g. a drag).
      const timer = window.setTimeout(() => release?.(), 600);
      release = () => {
        window.clearTimeout(timer);
        document.removeEventListener('click', swallow, true);
        release = null;
      };
      document.addEventListener('click', swallow, true);
      onDismiss();
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => {
      document.removeEventListener('mousedown', onMouseDown);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
}
